import { useRef, useEffect, useState } from "react";
import SEO from "../components/SEO";
import { motion, useInView, animate } from "framer-motion";
import { ArrowRight, Heart, CheckCircle, Menu, ChevronDown, BarChart3, Lightbulb, Shield, Target, Home, Users, TrendingUp, DollarSign, BookOpen, Gift, Headphones, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Link, useNavigate } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_5ead2d97-7ad4-48d6-8ee4-b13556106f69/artifacts/jd65gb5i_CityWorth%20Logo.png";
const ZOOM_WEBINAR_LINK = "https://zoom.us/webinar/register/WN__fC109gdSYC3mFsF8cBJJA";

const IMG_HERO = "https://customer-assets.emergentagent.com/job_38ff7071-66f9-4d88-af35-8aea95a1b115/artifacts/bd4gpi71_9c1704e9-a2fd-4116-bbdf-40383e1a2ed8.png";
const IMG_HOMEOWNERS = "https://static.prod-images.emergentagent.com/jobs/38ff7071-66f9-4d88-af35-8aea95a1b115/images/bfabe2654cf8b54a941b5966efc66462ff7e20d46528db79befdc8c0131619b4.png";
const IMG_MAIL = "https://static.prod-images.emergentagent.com/jobs/38ff7071-66f9-4d88-af35-8aea95a1b115/images/b9c2beacd5eae5ca06cf3224b44b41aa7ea1716854969f03d3b6f8923efb73a9.png";
const IMG_EMPLOYEES = "https://customer-assets.emergentagent.com/job_38ff7071-66f9-4d88-af35-8aea95a1b115/artifacts/8ioifnlc_12083dd0-2e04-417f-b9fc-1c44a904b94e.png";
const IMG_PARTNERSHIP = "https://static.prod-images.emergentagent.com/jobs/38ff7071-66f9-4d88-af35-8aea95a1b115/images/e6000bf5bbd370f98f860f678904ba4cc65c7402f594aeb77cfb9d043a2a1877.png";
const IMG_FUTURE = "https://static.prod-images.emergentagent.com/jobs/38ff7071-66f9-4d88-af35-8aea95a1b115/images/7cbbfc1f61ee2f812231094fe29b1387b5805441c627ae4a40fe819bef420505.png";

/* ── Animated section wrapper ── */
const Reveal = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/* ── Counter animation for the 3x stat ── */
const CountUp = ({ target, suffix = "x" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    if (isInView) {
      const controls = animate(0, target, {
        duration: 2,
        ease: "easeOut",
        onUpdate(value) {
          setDisplay(value.toFixed(target % 1 === 0 ? 0 : 1));
        },
      });
      return () => controls.stop();
    }
  }, [isInView, target]);

  return <span ref={ref}>{display}{suffix}</span>;
};

/* ── Sticky section nav items ── */
const NAV_SECTIONS = [
  { id: "hero", label: "Start" },
  { id: "origin", label: "Origin" },
  { id: "realization", label: "2019" },
  { id: "benefit", label: "The Benefit" },
  { id: "mail", label: "2025" },
  { id: "discovery", label: "Discovery" },
  { id: "epiphany", label: "Epiphany" },
  { id: "bigshift", label: "The Shift" },
  { id: "employers", label: "Employers" },
  { id: "future", label: "Future" },
  { id: "cta", label: "Get Started" },
];

const CityWorthStoryPage = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");
  const [showStickyNav, setShowStickyNav] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyNav(window.scrollY > 600);
      const sections = NAV_SECTIONS.map(s => document.getElementById(s.id));
      for (let i = sections.length - 1; i >= 0; i--) {
        if (sections[i] && sections[i].getBoundingClientRect().top <= 200) {
          setActiveSection(NAV_SECTIONS[i].id);
          break;
        }
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const goToSection = (direction) => {
    const currentIdx = NAV_SECTIONS.findIndex(s => s.id === activeSection);
    const nextIdx = direction === "down" ? Math.min(currentIdx + 1, NAV_SECTIONS.length - 1) : Math.max(currentIdx - 1, 0);
    scrollTo(NAV_SECTIONS[nextIdx].id);
  };

  const menuItems = [
    { label: "Home", action: () => navigate("/") },
    { label: "How It Works", action: () => navigate("/how-it-works") },
    { label: "Retention Programs", action: () => navigate("/retention-programs") },
    { label: "Retention Analysis", action: () => navigate("/retention-analysis") },
    { label: "Employee Experience", action: () => navigate("/employee-experience") },
    { label: "Our Story", action: () => navigate("/our-story") },
    { label: "Retention Calculator", action: () => navigate("/retention-calculator") },
    { label: "Events", action: () => navigate("/events") },
    { label: "Book a Call", action: () => navigate("/book-a-call") },
    { label: "Careers", action: () => navigate("/careers") },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="cityworth-story-page">
      <SEO title="The Big Shift - Our Story" description="Homeowners stay 3x longer than renters. Discover how CityWorth uncovered one of the most important insights in employee retention." />

      {/* ── Top Navigation ── */}
      <nav className="bg-white border-b border-slate-100 py-2">
        <div className="container-main flex items-center justify-between py-8 md:py-10">
          <Link to="/"><img src={LOGO_URL} alt="CityWorth" className="h-10 md:h-12" /></Link>
          <div className="flex items-center gap-4">
            <Link to="/get-started"><Button className="btn-primary text-white px-6 py-2.5">Get Started</Button></Link>
            <div className="relative">
              <Button variant="outline" onClick={() => setIsMenuOpen(!isMenuOpen)} className="flex items-center gap-2 border-slate-300 px-4 py-2.5">
                <Menu className="w-5 h-5" /><span className="hidden sm:inline">Menu</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`} />
              </Button>
              {isMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setIsMenuOpen(false)} />
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50">
                    {menuItems.map((item, i) => (
                      <button key={i} onClick={() => { item.action(); setIsMenuOpen(false); }} className="w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 hover:text-slate-900 transition-colors">{item.label}</button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* ── Floating Section Arrows ── */}
      <div className="fixed right-6 top-1/2 -translate-y-1/2 z-40 hidden md:flex flex-col gap-3">
        <button
          onClick={() => goToSection("up")}
          className="w-10 h-10 rounded-full bg-white/80 border border-slate-200 shadow-sm flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-white hover:shadow-md transition-all"
          aria-label="Previous section"
        >
          <ChevronDown className="w-5 h-5 rotate-180" />
        </button>
        <button
          onClick={() => goToSection("down")}
          className="w-10 h-10 rounded-full bg-white/80 border border-slate-200 shadow-sm flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-white hover:shadow-md transition-all"
          aria-label="Next section"
        >
          <ChevronDown className="w-5 h-5" />
        </button>
      </div>

      {/* ═══════════════════════════════════════════════
          SECTION 1 — HERO
      ═══════════════════════════════════════════════ */}
      <section id="hero" className="relative min-h-[90vh] flex items-center hero-bg-light">
        <div className="absolute inset-0 grid-pattern opacity-40" />
        <div className="container-main relative z-10 pt-8 pb-24 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-50 border border-red-100 mb-8"
            >
              <span className="w-2 h-2 rounded-full bg-red-600" />
              <span className="text-sm font-semibold text-red-700 tracking-wide uppercase">CityWorth Presents</span>
            </motion.div>

            <h1 className="text-5xl sm:text-6xl lg:text-8xl font-black text-slate-900 leading-[0.95] mb-8 tracking-tight">
              The Big <span className="gradient-text">Shift</span>
            </h1>

            <p className="text-xl md:text-2xl text-slate-700 font-semibold mb-4 max-w-2xl">
              Homeownership is not just a benefit.<br />It is a retention strategy.
            </p>

            <p className="text-lg text-slate-500 mb-10 max-w-xl leading-relaxed">
              CityWorth started by helping underserved families become homeowners. What we discovered along the way may change how companies think about retention forever.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/get-started">
                <Button size="lg" className="btn-primary text-white px-8 py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all hover:scale-105">
                  Book a Retention Strategy Session <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img src={IMG_HERO} alt="Employees and families celebrating homeownership" className="w-full h-[300px] lg:h-[450px] object-cover" />
            </div>
          </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 12, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 rounded-full border-2 border-slate-300 flex justify-center pt-2">
            <div className="w-1.5 h-3 rounded-full bg-red-600" />
          </div>
        </motion.div>
      </section>

      {/* ═══════════════════════════════════════════════
          SECTION 2 — ORIGIN STORY
      ═══════════════════════════════════════════════ */}
      <section id="origin" className="py-28 bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Reveal>
                <div className="inline-flex items-center gap-3 mb-8">
                  <div className="w-12 h-[2px] bg-red-600" />
                  <span className="text-sm font-bold text-red-600 uppercase tracking-widest">2012</span>
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 className="text-3xl md:text-5xl font-black text-slate-900 leading-tight mb-8">
                  It started with a mission to serve the underserved.
                </h2>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="space-y-5 text-lg text-slate-600 leading-relaxed">
                  <p>CityWorth began in 2012 with a simple but powerful mission: help people who had been overlooked become homeowners.</p>
                  <p>Many had low credit. Many thought buying a home was out of reach. We stepped in to help them improve their financial position, qualify for a loan, and move toward homeownership.</p>
                  <p className="text-slate-900 font-semibold">But we were never just helping people buy houses.</p>
                </div>
              </Reveal>
              <Reveal delay={0.3}>
                <div className="mt-8 space-y-3">
                  {["We were helping them build stability.", "We were helping them change their lives.", "We were helping them create long-term wealth."].map((line, i) => (
                    <p key={i} className="text-xl text-slate-800 font-bold flex items-center gap-3">
                      <span className="w-2 h-2 rounded-full bg-red-600 flex-shrink-0" />{line}
                    </p>
                  ))}
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img src={IMG_HOMEOWNERS} alt="Happy homeowners" className="w-full h-[450px] object-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          SECTION 3 — THE FIRST REALIZATION (2019)
      ═══════════════════════════════════════════════ */}
      <section id="realization" className="py-28 bg-slate-50 overflow-hidden">
        <div className="container-main max-w-4xl text-center">
          <Reveal>
            <div className="inline-flex items-center gap-3 mb-8">
              <div className="w-12 h-[2px] bg-red-600" />
              <span className="text-sm font-bold text-red-600 uppercase tracking-widest">2019</span>
              <div className="w-12 h-[2px] bg-red-600" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="text-3xl md:text-6xl font-black text-slate-900 leading-tight mb-10">
              An idea that changed <span className="gradient-text">everything.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-xl text-slate-600 mb-12 max-w-2xl mx-auto">
              During a brainstorming session about how to better serve our customers, we asked a question that would change the direction of our company.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="bg-white rounded-3xl p-10 md:p-14 border border-slate-200 shadow-lg max-w-2xl mx-auto">
              <p className="text-2xl md:text-3xl text-slate-800 font-bold mb-4">
                What if employers could help their employees become homeowners?
              </p>
              <p className="text-2xl md:text-3xl font-black gradient-text">
                What if homeownership could become an employee benefit?
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          SECTION 4 — BUILDING THE BENEFIT
      ═══════════════════════════════════════════════ */}
      <section id="benefit" className="py-28 bg-white">
        <div className="container-main">
          <Reveal>
            <h2 className="text-3xl md:text-5xl font-black text-slate-900 leading-tight mb-6 text-center max-w-3xl mx-auto">
              So we built a new kind of <span className="gradient-text">employee benefit.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-xl text-slate-600 text-center max-w-2xl mx-auto mb-16">
              Make homebuying easier and more affordable for employees while giving companies something more meaningful than another standard benefit.
            </p>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6 max-w-5xl mx-auto mb-16">
            {[
              { icon: BookOpen, label: "Education", color: "bg-blue-50 text-blue-600" },
              { icon: Gift, label: "Homebuying Rewards", color: "bg-green-50 text-green-600" },
              { icon: DollarSign, label: "Discounts", color: "bg-red-50 text-red-600" },
              { icon: Headphones, label: "Real Human Support", color: "bg-purple-50 text-purple-600" },
              { icon: Home, label: "Path to Ownership", color: "bg-blue-50 text-blue-600" },
            ].map((item, i) => (
              <Reveal key={i} delay={0.1 * i}>
                <div className="bg-white rounded-2xl p-6 text-center border border-slate-200 shadow-sm card-hover">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4 ${item.color.split(' ')[0]}`}>
                    <item.icon className={`w-7 h-7 ${item.color.split(' ')[1]}`} />
                  </div>
                  <p className="font-bold text-slate-900 text-sm">{item.label}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.3}>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img src={IMG_PARTNERSHIP} alt="CityWorth partnerships" className="w-full h-[350px] object-cover" />
              </div>
              <div>
                <p className="text-6xl md:text-8xl font-black text-red-600 mb-2">50+</p>
                <p className="text-2xl text-slate-700 font-semibold">organizations partnered with CityWorth to offer this solution.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          SECTION 5 — RETURNING TO DIRECT MAIL
      ═══════════════════════════════════════════════ */}
      <section id="mail" className="py-28 bg-slate-50">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <Reveal>
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img src={IMG_MAIL} alt="Direct mail strategy" className="w-full h-[400px] object-cover" />
              </div>
            </Reveal>
            <div>
              <Reveal>
                <div className="inline-flex items-center gap-3 mb-8">
                  <div className="w-12 h-[2px] bg-red-600" />
                  <span className="text-sm font-bold text-red-600 uppercase tracking-widest">2025</span>
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 className="text-3xl md:text-5xl font-black text-slate-900 leading-tight mb-8">
                  Then we went back to what had always worked.
                </h2>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="space-y-5 text-lg text-slate-600 leading-relaxed">
                  <p>We wanted to improve employee engagement. So we returned to direct mail.</p>
                  <p>The plan was to send mail to employees' homes so they and their household would know about their homebuying benefit.</p>
                  <p className="text-slate-900 font-semibold">To do that, we received employee address data.</p>
                  <p>At first, the goal was simple: figure out which employees owned a home and which were renting so we could decide who to target.</p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          SECTION 6 — THE DISCOVERY
      ═══════════════════════════════════════════════ */}
      <section id="discovery" className="py-28 bg-slate-900">
        <div className="container-main max-w-4xl">
          <Reveal>
            <h2 className="text-3xl md:text-6xl font-black text-white leading-tight mb-10 text-center">
              But the data showed us something <span className="text-red-400">bigger.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-xl text-white/60 text-center mb-6 max-w-2xl mx-auto">
              As we reviewed public real estate records against employee data, patterns started to appear.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-lg text-white/40 text-center mb-12 max-w-2xl mx-auto">
              Then we noticed employee start dates. And that led to a bigger question:
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-10 md:p-14 text-center border border-white/10">
              <p className="text-2xl md:text-4xl font-black text-white">
                What is the relationship between housing status and <span className="text-red-400">employee tenure</span>?
              </p>
              <div className="w-16 h-1 bg-red-500 mx-auto mt-6" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          SECTION 6.5 — EMPLOYEES PHOTO
      ═══════════════════════════════════════════════ */}
      <section className="py-16 bg-slate-900">
        <div className="container-main">
          <Reveal>
            <img src={IMG_EMPLOYEES} alt="Happy employees" className="w-full max-w-3xl mx-auto rounded-2xl shadow-lg object-cover h-[400px]" />
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          SECTION 7 — THE EPIPHANY (KEYNOTE MOMENT)
      ═══════════════════════════════════════════════ */}
      <section id="epiphany" className="pt-28">
        <div className="min-h-[80vh] bg-gradient-to-b from-white via-red-50/30 to-white flex items-center justify-center relative overflow-hidden">
          {/* Background accents */}
          <div className="absolute inset-0">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-red-100/40 rounded-full blur-[150px]" />
            <div className="absolute top-1/3 left-1/3 w-[300px] h-[300px] bg-amber-100/30 rounded-full blur-[100px]" />
          </div>

          <div className="container-main relative z-10 text-center pt-48 pb-24">
            <Reveal>
              <p className="text-lg md:text-xl text-amber-600 font-semibold uppercase tracking-widest mb-4">
                That was the moment everything changed
              </p>
              <div className="w-20 h-1 bg-gradient-to-r from-red-600 to-amber-500 mx-auto mb-10 rounded-full" />
            </Reveal>

            <Reveal delay={0.2}>
              <div className="mb-14">
                <p className="text-xl md:text-2xl text-slate-500 mb-6">Homeowners stay</p>
                <div className="relative inline-block">
                  <div className="text-8xl sm:text-9xl md:text-[12rem] font-black leading-none tracking-tight bg-gradient-to-br from-red-600 via-red-500 to-amber-500 bg-clip-text text-transparent">
                    <CountUp target={3} suffix="x" />
                  </div>
                </div>
                <p className="text-2xl md:text-3xl text-slate-900 font-bold mt-6">longer than renters.</p>
              </div>
            </Reveal>

            <Reveal delay={0.3}>
              <p className="text-lg text-slate-500 mb-10 max-w-xl mx-auto">We dug deeper across company after company. The pattern was undeniable.</p>
            </Reveal>

            <Reveal delay={0.4}>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-4xl mx-auto">
                {[
                  { multiplier: "3x", label: "Company A" },
                  { multiplier: "3.2x", label: "Company B" },
                  { multiplier: "2.8x", label: "Company C" },
                  { multiplier: "4x", label: "Company D" },
                ].map((item, i) => (
                  <div key={i} className="bg-white rounded-2xl p-6 text-center border border-slate-200 shadow-md hover:shadow-lg transition-shadow">
                    <div className="text-4xl font-black bg-gradient-to-br from-red-600 to-amber-500 bg-clip-text text-transparent mb-1">{item.multiplier}</div>
                    <div className="text-sm text-slate-400 mb-4">{item.label}</div>
                    <div className="space-y-2.5">
                      <div className="flex items-center gap-2">
                        <div className="text-[10px] text-slate-400 w-14 text-right font-medium">Owners</div>
                        <div className="flex-1 bg-slate-100 rounded-full h-3">
                          <motion.div
                            className="bg-gradient-to-r from-red-500 to-amber-500 h-3 rounded-full"
                            initial={{ width: 0 }}
                            whileInView={{ width: "85%" }}
                            viewport={{ once: true }}
                            transition={{ duration: 1.5, delay: 0.5 + i * 0.2 }}
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-[10px] text-slate-400 w-14 text-right font-medium">Renters</div>
                        <div className="flex-1 bg-slate-100 rounded-full h-3">
                          <motion.div
                            className="bg-slate-300 h-3 rounded-full"
                            initial={{ width: 0 }}
                            whileInView={{ width: `${85 / parseFloat(item.multiplier)}%` }}
                            viewport={{ once: true }}
                            transition={{ duration: 1.5, delay: 0.7 + i * 0.2 }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.6}>
              <p className="text-xl text-slate-500 mt-12 font-medium">
                Different companies. Different workforces. <span className="text-red-600 font-bold">Same pattern.</span>
              </p>
            </Reveal>
          </div>
        </div>

        {/* Post-epiphany statement */}
        <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 py-20">
          <div className="container-main max-w-3xl text-center">
            <Reveal>
              <p className="text-xl text-white/50 mb-4">This was not a theory. It was not a one-time anomaly.</p>
              <p className="text-2xl md:text-3xl text-white font-bold mb-6">It was a repeated pattern in real workforce data.</p>
              <div className="w-20 h-1 bg-gradient-to-r from-red-500 to-amber-500 mx-auto mb-6 rounded-full" />
              <p className="text-xl text-amber-400 font-semibold">
                We had uncovered one of the most important insights in employee retention.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          SECTION 8 — WHAT THE BIG SHIFT MEANS
      ═══════════════════════════════════════════════ */}
      <section id="bigshift" className="py-28 bg-white">
        <div className="container-main">
          <Reveal>
            <h2 className="text-4xl md:text-7xl font-black text-slate-900 text-center mb-6">
              This is <span className="gradient-text">The Big Shift.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-xl text-slate-600 text-center max-w-2xl mx-auto mb-20">
              Moving from recruiting-first thinking to retention through homeownership.
            </p>
          </Reveal>

          {/* Comparison blocks */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-16">
            <Reveal>
              <div className="rounded-2xl p-8 bg-slate-50 border border-slate-200">
                <p className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-6">Old Thinking</p>
                <div className="space-y-4">
                  {["Recruiting-first", "Standard perks", "Turnover response", "Replace faster"].map((item, i) => (
                    <p key={i} className="text-lg text-slate-400 font-medium line-through decoration-slate-300">{item}</p>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="rounded-2xl p-8 bg-red-50 border border-red-100">
                <p className="text-sm font-bold text-red-600 uppercase tracking-widest mb-6">The Big Shift</p>
                <div className="space-y-4">
                  {["Stability-first", "Life-changing support", "Retention strategy", "Help people stay"].map((item, i) => (
                    <p key={i} className="text-lg text-slate-900 font-bold flex items-center gap-3">
                      <ChevronRight className="w-5 h-5 text-red-600 flex-shrink-0" />{item}
                    </p>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.2}>
            <div className="max-w-2xl mx-auto text-center space-y-3">
              {[
                "Homeownership is a stability strategy.",
                "It is a wealth-building strategy.",
                "And now, it is a retention strategy.",
              ].map((line, i) => (
                <p key={i} className="text-xl text-slate-800 font-bold">{line}</p>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          SECTION 9 — WHAT THIS MEANS FOR EMPLOYERS
      ═══════════════════════════════════════════════ */}
      <section id="employers" className="py-28 bg-slate-50">
        <div className="container-main">
          <Reveal>
            <h2 className="text-3xl md:text-5xl font-black text-slate-900 text-center mb-6">
              What this means for employers.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-xl text-slate-600 text-center max-w-2xl mx-auto mb-16">
              Companies need to think beyond traditional benefits. The future is not just better perks. It is better foundations.
            </p>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 max-w-5xl mx-auto mb-16">
            {[
              { icon: TrendingUp, stat: "Stronger", label: "Retention" },
              { icon: Users, stat: "Reduced", label: "Turnover" },
              { icon: Shield, stat: "More Stable", label: "Teams" },
              { icon: Target, stat: "Less", label: "Recruiting Pressure" },
              { icon: DollarSign, stat: "Long-term", label: "Cost Savings" },
            ].map((item, i) => (
              <Reveal key={i} delay={0.05 * i}>
                <div className="bg-white rounded-2xl p-6 text-center border border-slate-200 shadow-sm card-hover">
                  <item.icon className="w-8 h-8 text-red-600 mx-auto mb-3" />
                  <p className="text-lg font-black text-slate-900">{item.stat}</p>
                  <p className="text-sm text-slate-500">{item.label}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.3}>
            <div className="bg-white rounded-3xl p-10 md:p-14 max-w-3xl mx-auto text-center border border-slate-200 shadow-sm">
              <p className="text-lg text-slate-500 mb-4">This is not abstract.</p>
              <p className="text-2xl text-slate-900 font-bold">
                It came from real workforce data and repeated patterns across companies.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          SECTION 10 — THE FUTURE
      ═══════════════════════════════════════════════ */}
      <section id="future" className="py-28 bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Reveal>
                <h2 className="text-3xl md:text-5xl font-black text-slate-900 leading-tight mb-8">
                  This is the future of <span className="gradient-text">employee retention.</span>
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="space-y-5 text-lg text-slate-600 leading-relaxed">
                  <p>CityWorth started by helping underserved families become homeowners. That mission led us to employers. That work led us to data. And that data led us to a discovery that changed our entire view of retention.</p>
                  <p className="text-xl text-slate-900 font-bold">Homeowners stay 3x longer than renters.</p>
                  <p>Now we are helping companies act on that insight.</p>
                </div>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="mt-8 space-y-4">
                  {[
                    "They will not just offer jobs. They will help employees build roots.",
                    "They will not just react to turnover. They will reduce it at the source.",
                    "They will not just follow the old playbook. They will lead the next one.",
                  ].map((line, i) => (
                    <p key={i} className="text-lg text-slate-700 font-semibold flex items-start gap-3">
                      <ChevronRight className="w-5 h-5 text-red-600 flex-shrink-0 mt-1" />{line}
                    </p>
                  ))}
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img src={IMG_FUTURE} alt="The future of retention" className="w-full h-[450px] object-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          SECTION 11 — CLOSING CTA
      ═══════════════════════════════════════════════ */}
      <section id="cta" className="py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-600 via-red-700 to-blue-800" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-white/10 via-transparent to-transparent" />

        <div className="container-main relative z-10 text-center max-w-3xl mx-auto">
          <Reveal>
            <h2 className="text-4xl md:text-6xl font-black text-white mb-6">
              See The Big Shift for yourself.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-xl text-white/80 mb-12 max-w-xl mx-auto">
              Discover how CityWorth is helping employers rethink retention through homeownership, workforce stability, and a new kind of employee benefit.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="flex justify-center">
              <Link to="/get-started">
                <Button size="lg" className="bg-white hover:bg-slate-100 text-slate-900 px-10 py-7 text-xl font-bold shadow-2xl transition-all hover:scale-105">
                  Talk to Our Team <ArrowRight className="ml-2 w-6 h-6" />
                </Button>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="py-12 bg-white border-t border-slate-200">
        <div className="container-main flex flex-col md:flex-row items-center justify-between gap-6">
          <Link to="/"><img src={LOGO_URL} alt="CityWorth" className="h-8" /></Link>
          <div className="flex flex-wrap items-center justify-center gap-6">
            <Link to="/" className="text-slate-600 hover:text-slate-900">Home</Link>
            <Link to="/how-it-works" className="text-slate-600 hover:text-slate-900">How It Works</Link>
            <Link to="/retention-programs" className="text-slate-600 hover:text-slate-900">Programs</Link>
            <Link to="/employee-experience" className="text-slate-600 hover:text-slate-900">Employee Experience</Link>
            <Link to="/our-story" className="text-slate-600 hover:text-slate-900">Our Story</Link>
            <Link to="/events" className="text-slate-600 hover:text-slate-900">Events</Link>
          </div>
          <p className="text-slate-500 text-sm">&copy; {new Date().getFullYear()} CityWorth</p>
        </div>
      </footer>
    </div>
  );
};

export default CityWorthStoryPage;
